import React, { useState, useEffect, useRef } from 'react';
import framework from 'utils/framework';
const { wrapFunctionalComponent } = framework;
import LanguageLocaleUtils from 'utils/LanguageLocale';
import userUtils from 'utils/User';
import {
    Box, Flex, Text, Button, Link, Grid, Divider
} from 'components/ui';
import InputEmail from 'components/Inputs/InputEmail/InputEmail';
import PasswordRevealInput from 'components/Inputs/PasswordRevealInput';
import ErrorList from 'components/ErrorList';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import externalAuthService from 'services/api/authentication/externalAuth';
import UserActions from 'actions/UserActions';
import store from 'Store';
import Actions from 'Actions';
import Chevron from 'components/Chevron/Chevron';
import UrlUtils from 'utils/Url';
import {
    borders, colors, space, fontSizes, fontWeights, lineHeights
} from 'style/config';
import formValidator from 'utils/FormValidator';
import ErrorConstantsUtils from 'utils/ErrorConstants';
import LoginEN from 'pages/Auth/locales/Login_en_US';
import LoginFR from 'pages/Auth/locales/Login_fr_CA';

const getText = LanguageLocaleUtils.getLocalisedDataFromResource(LoginEN, LoginFR);
const { ERROR_CODES } = ErrorConstantsUtils;

/**
 * Login Page
 * Full-screen login page that handles both regular login and OAuth2 external authentication
 * Supports two flows:
 *
 * OAuth2 Flow (when client_id and redirect_uri params present):
 * 1. User is redirected here from backend /oauth2/authorize endpoint (302 redirect)
 * 2. URL contains OAuth2 protocol parameters for external authentication
 * 3. User enters email and password
 * 4. Submit credentials to backend /oauth2/login API endpoint
 * 5. On success: receive session_id, token, hasIdentity
 * 6. Trigger standard Sephora login flow using UserActions.signIn
 * 7. After full login completes, navigate to /authorize page for authorization
 *
 * Regular Login Flow (when OAuth2 params not present):
 * 1. User enters email and password
 * 2. Call UserActions.signIn directly with regular login callback
 * 3. Redirect to home page or intended destination after successful login
 */
const Login = () => {
    const emailInput = useRef(null);
    const passwordInput = useRef(null);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessages, setErrorMessages] = useState([]);
    const [isAutoLoginInProgress, setIsAutoLoginInProgress] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [externalAuthParams, setExternalAuthParams] = useState({
        responseType: '',
        clientId: '',
        redirectUri: '',
        scope: '',
        state: '',
        codeChallenge: '',
        codeChallengeMethod: '',
        nonce: '',
        prompt: '',
        display: '',
        maxAge: '',
        uiLocales: '',
        idTokenHint: '',
        loginHint: '',
        acrValues: ''
    });

    // Determine if this is an OAuth2 flow based on presence of required OAuth2 parameters
    const isOAuth2Flow = !!(externalAuthParams.clientId && externalAuthParams.redirectUri);

    // Check if logout is in progress on initial load
    useEffect(() => {
        // Check if RefreshToken service has flagged for signout on init
        if (window.Sephora?.Util?.RefreshToken?.isSignOutOnInit) {
            setIsLoggingOut(true);
            store.dispatch(Actions.showInterstice(true));
        }
    }, []);

    // Populate email and external auth params from URL on client-side
    // eslint-disable-next-line complexity
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        const params = {
            responseType: urlParams.get('response_type') || '',
            clientId: urlParams.get('client_id') || '',
            redirectUri: urlParams.get('redirect_uri') || '',
            scope: urlParams.get('scope') || '',
            state: urlParams.get('state') || '',
            codeChallenge: urlParams.get('code_challenge') || '',
            codeChallengeMethod: urlParams.get('code_challenge_method') || '',
            nonce: urlParams.get('nonce') || '',
            prompt: urlParams.get('prompt') || '',
            display: urlParams.get('display') || '',
            maxAge: urlParams.get('max_age') || '',
            uiLocales: urlParams.get('ui_locales') || '',
            idTokenHint: urlParams.get('id_token_hint') || '',
            loginHint: urlParams.get('login_hint') || '',
            acrValues: urlParams.get('acr_values') || ''
        };

        setExternalAuthParams(params);

        // Pre-populate email if login_hint is provided
        if (params.loginHint) {
            setEmail(params.loginHint);
        }

        // Store external auth params in localStorage for use after login (OAuth2 flow only)
        if (params.clientId && params.redirectUri) {
            Storage.local.setItem(LOCAL_STORAGE.EXTERNAL_AUTH_PARAMS, params);
        }

        // Handle logged-in users
        const handleLoggedInUser = async () => {
            await userUtils.validateUserStatus();

            const isSignedIn = userUtils.isSignedIn();

            if (isSignedIn) {
                if (params.clientId && params.redirectUri) {
                    // OAuth2 flow: Auto-login with stored credentials
                    await attemptAutoLogin(params);
                } else {
                    // Non-OAuth2 flow: Redirect to home page
                    UrlUtils.redirectTo('/');
                }
            }
        };

        handleLoggedInUser();
    }, []);

    /**
     * Attempt automatic login for already logged-in users with OAuth2 params
     * Uses stored email and access token instead of password
     */
    const attemptAutoLogin = async params => {
        try {
            setIsAutoLoginInProgress(true);
            store.dispatch(Actions.showInterstice(true));

            // Get stored credentials
            const storedEmail = userUtils.getProfileEmail();
            const accessToken = Storage.local.getItem(LOCAL_STORAGE.AUTH_ACCESS_TOKEN);

            if (!storedEmail || !accessToken) {
                throw new Error('Missing stored credentials');
            }

            // Call external auth login with access token instead of password
            const externalAuthResponse = await externalAuthService.externalAuthLogin({
                email: storedEmail,
                accessToken, // Use access token instead of password
                ...params
            });

            const sessionId = externalAuthResponse?.sessionId;

            if (!sessionId) {
                throw new Error('No session ID received');
            }

            // Store session_id for authorization grant page
            Storage.local.setItem(LOCAL_STORAGE.EXTERNAL_AUTH_SESSION_ID, sessionId);

            // Navigate to authorize page
            store.dispatch(Actions.showInterstice(false));
            UrlUtils.redirectTo(`/authorize?session_id=${sessionId}`);
        } catch (error) {
            // Auto-login failed - show modal and allow manual login
            store.dispatch(Actions.showInterstice(false));
            setIsAutoLoginInProgress(false);

            const redirectUrl = window.location.pathname + window.location.search;

            // Show verification failed modal
            store.dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    title: getText('verificationFailedTitle'),
                    message: getText('verificationFailedMessage'),
                    buttonText: getText('verificationFailedButton'),
                    callback: () => {
                        // Close modal and show loading state
                        store.dispatch(Actions.showInfoModal({ isOpen: false }));
                        setIsLoggingOut(true);
                        store.dispatch(Actions.showInterstice(true));
                        // Log user out and stay on current page to show login form
                        store.dispatch(UserActions.signOut(redirectUrl, false, false));
                    },
                    cancelCallback: () => {
                        // Close modal and show loading state
                        store.dispatch(Actions.showInfoModal({ isOpen: false }));
                        setIsLoggingOut(true);
                        store.dispatch(Actions.showInterstice(true));
                        // Log user out and stay on current page to show login form
                        store.dispatch(UserActions.signOut(redirectUrl, false, false));
                    },
                    showCloseButton: false
                })
            );
        }
    };

    const handleEmailChange = e => {
        setEmail(e.target.value);
    };

    const handlePasswordChange = e => {
        setPassword(e.target.value);
    };

    const validatePassword = pass => {
        if (formValidator.isEmpty(pass)) {
            return getText('enterPasswordErrorMessage');
        }

        if (!formValidator.isValidLength(pass, 6, 12)) {
            return getText('passwordLengthInvalid');
        }

        return null;
    };

    const isValid = () => {
        const fieldsForValidation = [emailInput.current, passwordInput.current];
        const errors = formValidator.getErrors(fieldsForValidation);
        const topLevelErrors = [];

        // For OAuth2 flow, validate required external auth parameters
        if (isOAuth2Flow) {
            if (!externalAuthParams.clientId) {
                topLevelErrors.push('Missing required parameter: client_id');
            }

            if (!externalAuthParams.redirectUri) {
                topLevelErrors.push('Missing required parameter: redirect_uri');
            }
        }

        if (topLevelErrors.length) {
            setErrorMessages(topLevelErrors);
        }

        return !errors.fields.length && !topLevelErrors.length;
    };

    const handleSubmit = async e => {
        e.preventDefault();
        setErrorMessages([]);

        if (!isValid()) {
            return;
        }

        store.dispatch(Actions.showInterstice(true));

        try {
            if (isOAuth2Flow) {
                // OAuth2 External Auth Flow - Call external auth login endpoint
                await handleExternalAuthLogin();
            } else {
                // Regular Login Flow - Call standard login directly
                await handleRegularLogin();
            }
        } catch (error) {
            const errorMessage = error?.errorMessage || error?.message || 'Login failed. Please try again.';

            setErrorMessages([errorMessage]);
            store.dispatch(Actions.showInterstice(false));
        }
    };

    /**
     * Handle regular login flow
     * Standard Sephora login without OAuth2 external authentication
     */
    const handleRegularLogin = async () => {
        const successCallback = () => {
            // After successful login, redirect to home page or intended destination
            store.dispatch(Actions.showInterstice(false));
            UrlUtils.redirectTo('/');
        };

        const errorCallback = json => {
            // Handle errors the same way as SignInModal
            if (json?.errorMessages) {
                if (json?.errorCode === ERROR_CODES.TS_HEADER_ERROR) {
                    setErrorMessages([getText('unableToLogYouIn')]);
                } else if (json.errorMessages[0]) {
                    setErrorMessages(json.errorMessages);
                } else if (json.responseStatus >= 400) {
                    setErrorMessages([getText('genericErrorMessage')]);
                }

                throw { errorMessage: json.errorMessages[0] };
            }
        };

        await store.dispatch(
            UserActions.signIn(email, password, true, false, successCallback, errorCallback, false, null, false, false, false, false)
        );
    };

    /**
     * Handle external auth login flow
     * Authenticates user and initiates OAuth2.0 authorization flow for third-party integrations
     * Calls backend /oauth2/login endpoint with credentials and OAuth2 protocol parameters
     */
    const handleExternalAuthLogin = async () => {
        try {
            // Step 1: Call external auth login API with OAuth2 protocol parameters
            // Note: camelCase params are converted to underscore format as per OAuth2 spec
            const externalAuthResponse = await externalAuthService.externalAuthLogin({
                email,
                password,
                ...externalAuthParams
            });

            // Expected response: { sessionId, userId, message, token, hasIdentity }
            const sessionId = externalAuthResponse?.sessionId;

            if (!sessionId) {
                throw { errorMessage: getText('checkEmailPassword') };
            }

            // Store session_id in localStorage for use in authorization grant page
            Storage.local.setItem(LOCAL_STORAGE.EXTERNAL_AUTH_SESSION_ID, sessionId);

            // Step 2: Complete standard Sephora login flow (generateTokens, profile fetch, etc.)
            // This ensures the user is fully authenticated before proceeding to authorization
            const successCallback = () => {
                // After successful login, navigate to authorize page
                store.dispatch(Actions.showInterstice(false));
                UrlUtils.redirectTo(`/authorize?session_id=${sessionId}`);
            };

            if (userUtils.isSignedIn()) {
                successCallback();

                return;
            }

            await store.dispatch(
                UserActions.signIn(
                    email,
                    password,
                    true,
                    false,
                    successCallback,
                    json => {
                        // Handle errors the same way as SignInModal
                        if (json?.errorMessages) {
                            if (json?.errorCode === ERROR_CODES.TS_HEADER_ERROR) {
                                setErrorMessages([getText('unableToLogYouIn')]);
                            } else if (json.errorMessages[0]) {
                                setErrorMessages(json.errorMessages);
                            } else if (json.responseStatus >= 400) {
                                setErrorMessages([getText('genericErrorMessage')]);
                            }

                            throw { errorMessage: json.errorMessages[0] };
                        }
                    },
                    false,
                    null,
                    false,
                    false,
                    false,
                    { isIntegrationLoginAttempt: isOAuth2Flow } // Pass extra param to indicate this is an integration login attempt (OAuth2 flow)
                )
            );
        } catch (error) {
            // Handle 401 responses and OAuth2 error structure: {error: "access_denied", error_description: "email.not.present"}
            // Display user-friendly message for all auth errors
            const errorMessage = error?.error_description || error?.errorMessage || error?.message || getText('checkEmailPassword');
            setErrorMessages([errorMessage]);
            throw { errorMessage };
        }
    };

    const handleContinueAsGuest = async () => {
        try {
            if (isOAuth2Flow) {
                const externalAuthResponse = await externalAuthService.externalAuthLogin({
                    email: null, // No email for guest login
                    password: null, // No password for guest login
                    ...externalAuthParams
                });
                const sessionId = externalAuthResponse?.sessionId;

                if (!sessionId) {
                    throw { errorMessage: getText('checkEmailPassword') };
                }

                // Store session_id in localStorage for use in authorization grant page
                Storage.local.setItem(LOCAL_STORAGE.EXTERNAL_AUTH_SESSION_ID, sessionId);

                UrlUtils.redirectTo(`/authorize?session_id=${sessionId}&guest=true`);
            } else {
                // For regular login, redirect to home page
                UrlUtils.redirectTo('/');
            }
        } catch (error) {
            // Handle 401 responses and OAuth2 error structure: {error: "access_denied", error_description: "email.not.present"}
            // Display user-friendly message for all auth errors
            const errorMessage = error?.error_description || error?.errorMessage || error?.message || getText('checkEmailPassword');
            setErrorMessages([errorMessage]);
            store.dispatch(Actions.showInterstice(false));
        }
    };

    const handleClose = () => {
        if (externalAuthParams.redirectUri) {
            // If there's a redirect URI (OAuth flow), redirect there
            UrlUtils.redirectTo(externalAuthParams.redirectUri);
        } else {
            // Otherwise, go back to previous page
            window.history.back();
        }
    };

    return (
        <div>
            <Box
                minHeight='100vh'
                display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='flex-start'
                css={styles.pageContainer}
            >
                <Flex css={styles.headerContainer}>
                    {/* Back Button */}
                    <Box
                        css={styles.backButton}
                        onClick={handleClose}
                    >
                        <Chevron
                            direction={'left'}
                            color={colors.black}
                            css={styles.chevron}
                        />
                    </Box>

                    {/* Logo */}
                    <Box css={styles.logoContainer}>
                        <img
                            src='/img/ufe/logo.svg'
                            alt='Sephora'
                            style={styles.logo}
                        />
                    </Box>
                </Flex>
                <Box css={styles.contentWrapper}>
                    {/* Don't show form while auto-login or logout is in progress */}
                    {isAutoLoginInProgress || isLoggingOut ? null : (
                        <>
                            {/* Page Title */}
                            <Box
                                maxWidth='400px'
                                width='100%'
                                css={styles.titleContainer}
                            >
                                <Text
                                    is='h1'
                                    fontSize={fontSizes.lg}
                                    fontWeight={fontWeights.bold}
                                    lineHeight={lineHeights.tight}
                                >
                                    {getText('pageTitle')}
                                </Text>
                            </Box>

                            {/* Benefits Section */}
                            <Box
                                maxWidth='400px'
                                width='100%'
                                css={styles.benefitsContainer}
                            >
                                <Grid
                                    gridTemplateColumns='repeat(3, 1fr)'
                                    css={styles.benefitsGrid}
                                >
                                    <Box
                                        display='flex'
                                        flexDirection='column'
                                        alignItems='center'
                                        textAlign='center'
                                    >
                                        <Box>
                                            <img
                                                src='/img/ufe/auth/makeup.svg'
                                                alt='makeup'
                                                style={styles.benefitIcon}
                                            />
                                        </Box>
                                        <Text
                                            fontSize={fontSizes.sm}
                                            lineHeight={lineHeights.tight}
                                            color={colors.black}
                                        >
                                            {getText('personalizedBeautyTitle')}
                                        </Text>
                                    </Box>
                                    <Box
                                        display='flex'
                                        flexDirection='column'
                                        alignItems='center'
                                        textAlign='center'
                                    >
                                        <Box>
                                            <img
                                                src='/img/ufe/auth/earn-points.svg'
                                                alt='earn points'
                                                style={styles.benefitIcon}
                                            />
                                        </Box>
                                        <Text
                                            fontSize={fontSizes.sm}
                                            lineHeight={lineHeights.tight}
                                            color={colors.black}
                                        >
                                            {getText('perksTitle')}
                                        </Text>
                                    </Box>
                                    <Box
                                        display='flex'
                                        flexDirection='column'
                                        alignItems='center'
                                        textAlign='center'
                                    >
                                        <Box>
                                            <img
                                                src='/img/ufe/auth/chat.svg'
                                                alt='chat'
                                                style={styles.benefitIcon}
                                            />
                                        </Box>
                                        <Text
                                            fontSize={fontSizes.sm}
                                            lineHeight={lineHeights.tight}
                                            color={colors.black}
                                        >
                                            {getText('adviceTitle')}
                                        </Text>
                                    </Box>
                                </Grid>
                            </Box>

                            {/* Form Container with 400px max-width */}
                            <Box
                                maxWidth='400px'
                                width='100%'
                            >
                                {/* Login Form */}
                                <form
                                    onSubmit={handleSubmit}
                                    noValidate={true}
                                >
                                    {/* Error Messages */}
                                    {errorMessages.length > 0 && (
                                        <Box css={styles.errorContainer}>
                                            <ErrorList errorMessages={errorMessages} />
                                        </Box>
                                    )}
                                    <Box css={styles.emailContainer}>
                                        <InputEmail
                                            id='login-email-input'
                                            name='email'
                                            ref={emailInput}
                                            login={email}
                                            onChange={handleEmailChange}
                                            placeholder={getText('emailPlaceholder')}
                                            autoFocus={!email}
                                            hideAsterisk={true}
                                        />
                                    </Box>

                                    <Box css={styles.passwordContainer}>
                                        <PasswordRevealInput
                                            id='login-password-input'
                                            name='password'
                                            ref={passwordInput}
                                            value={password}
                                            onChange={handlePasswordChange}
                                            placeholder={getText('passwordPlaceholder')}
                                            hideAsterisk={true}
                                            validate={validatePassword}
                                        />
                                    </Box>

                                    {/* Forgot Password Link */}
                                    <Box
                                        css={styles.forgotPasswordContainer}
                                        textAlign='right'
                                    >
                                        <Link
                                            href='/resetpassword'
                                            color={colors.blue}
                                            fontSize={fontSizes.sm}
                                        >
                                            {getText('forgotPassword')}
                                        </Link>
                                    </Box>

                                    {/* Terms and Privacy */}
                                    <Box css={styles.termsContainer}>
                                        <Text
                                            fontSize={fontSizes.xs}
                                            color={colors.gray}
                                            lineHeight={lineHeights.base}
                                        >
                                            {getText('termsPrefix')}
                                            <Link
                                                href='/terms-of-use'
                                                color={colors.blue}
                                                underline={true}
                                                fontWeight={fontWeights.bold}
                                            >
                                                {getText('termsOfUse')}
                                            </Link>
                                            {getText('termsAnd')}
                                            <Link
                                                href='/privacy-policy'
                                                color={colors.blue}
                                                underline={true}
                                            >
                                                {getText('privacyPolicy')}
                                            </Link>
                                        </Text>
                                    </Box>

                                    {/* Sign In Button */}
                                    <Button
                                        type='submit'
                                        width='100%'
                                        variant='primary'
                                        paddingY={space[3]}
                                    >
                                        {getText('signInButton')}
                                    </Button>

                                    {/* Divider with spacing */}
                                    <Box css={styles.dividerContainer}>
                                        <Divider css={styles.divider} />
                                    </Box>

                                    {/* Continue as Guest Button */}
                                    {Sephora.configurationSettings?.integrations_guest_allowed && (
                                        <Button
                                            type='button'
                                            width='100%'
                                            variant='secondary'
                                            onClick={handleContinueAsGuest}
                                            paddingY={space[3]}
                                        >
                                            {getText('continueAsGuest')}
                                        </Button>
                                    )}
                                </form>
                            </Box>
                        </>
                    )}
                </Box>
                {/* End Form Container */}
            </Box>
        </div>
    );
};

const styles = {
    pageContainer: {
        paddingLeft: space[3],
        paddingRight: space[3]
    },
    headerContainer: {
        position: 'relative',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'center',
        borderBottom: `${borders[1]} ${colors.lightGray}`
    },
    backButton: {
        position: 'absolute',
        top: space[5],
        left: space[5],
        cursor: 'pointer'
    },
    chevron: {
        width: space[4],
        height: space[4]
    },
    logoContainer: {
        marginBottom: space[5],
        marginTop: space[5]
    },
    logo: {
        height: space[5],
        width: 'auto'
    },
    contentWrapper: {
        marginTop: space[7]
    },
    titleContainer: {
        marginBottom: space[6]
    },
    benefitsContainer: {
        marginBottom: space[6]
    },
    benefitsGrid: {
        gap: space[4]
    },
    benefitIcon: {
        height: space[7],
        width: 'auto'
    },
    errorContainer: {
        marginBottom: space[4]
    },
    emailContainer: {
        marginBottom: space[4]
    },
    passwordContainer: {
        marginBottom: space[2]
    },
    forgotPasswordContainer: {
        marginBottom: space[5]
    },
    termsContainer: {
        marginBottom: space[3]
    },
    dividerContainer: {
        marginTop: space[4],
        marginBottom: space[4],
        height: space[2]
    },
    divider: {
        height: space[2]
    }
};

export default wrapFunctionalComponent(Login, 'Login');
